"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { Typography } from "@repo/ui/aops-components/typography";
import type { CoursePageSection, FullCourse } from "@repo/sanity-utils/query-types";
import { courseOfferingSelectionsAtom, learningFormatAtom, selectedCampusAtom } from "@/app/atoms/courseSelections";
import { getActiveOffering, resolveSelectedOfferingKey } from "@/lib/activeCourseOffering";
import { fetchCourseOfferingProductWithSchedule } from "@/app/actions/fetchCourseOfferingProductWithSchedule";
import { type CourseOfferingProduct, type CourseOfferingProductByKey } from "@/lib/courseOfferingProduct/courseOfferingProductData";
import { courseOfferingProductKey } from "@/lib/courseOfferingProduct/courseOfferingProductKey";
import { getKeyedCopy } from "@repo/utils/keyedCopy";
import { AddToCartButton } from "./AddToCartButton/AddToCartButton";
import { getFewSeatsRemainingThreshold, isFewSeatsRemaining } from "./FewSeatsRemainingBadge/fewSeatsRemaining";
import { FewSeatsRemainingBadge } from "./FewSeatsRemainingBadge/FewSeatsRemainingBadge";
import { getStartingSoonThresholdDays, isStartingSoon } from "./StartingSoonBadge/startingSoon";
import { StartingSoonBadge } from "./StartingSoonBadge/StartingSoonBadge";
import { CLASS_SCHEDULE_COPY } from "./ClassScheduleCopyConstants";
import { CLASS_INSTANCE_HASH_PREFIX, getClassInstanceElementId, isClassInstanceHash, scrollToClassInstance } from "./scrollToClassInstance";
import styles from "./Schedule.module.css";
type ScheduleProps = {
  course: NonNullable<FullCourse>;
  coursePageSection?: NonNullable<CoursePageSection>;
  initialPart?: string | null;
  courseOfferingProducts: CourseOfferingProductByKey;
};

/**
 * Render a field row.
 */
function FieldRow(t0) {
  const $ = _c(3);
  const {
    label,
    value
  } = t0;
  const t1 = value ?? "\u2014";
  let t2;
  if ($[0] !== label || $[1] !== t1) {
    t2 = <Typography variant="bodySmall">{label}: {t1}</Typography>;
    $[0] = label;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

/**
 * Render the schedule table.
 */
function ScheduleTable(t0) {
  const $ = _c(13);
  const {
    product,
    fewSeatsRemainingBadgeLabel,
    fewSeatsRemainingThreshold,
    startingSoonBadgeLabel,
    startingSoonThresholdDays
  } = t0;
  let t1;
  if ($[0] !== fewSeatsRemainingBadgeLabel || $[1] !== fewSeatsRemainingThreshold || $[2] !== product.classInstances || $[3] !== startingSoonBadgeLabel || $[4] !== startingSoonThresholdDays) {
    let t2;
    if ($[6] !== fewSeatsRemainingBadgeLabel || $[7] !== fewSeatsRemainingThreshold || $[8] !== startingSoonBadgeLabel || $[9] !== startingSoonThresholdDays) {
      t2 = classInstance => {
        const classMeta = classInstance.class;
        const schedule = classMeta?.schedule;
        const meetingTimes = schedule?.meetingTimes ?? [];
        const showFewSeatsRemaining = isFewSeatsRemaining(classInstance.seatsAvailable, fewSeatsRemainingThreshold);
        const showStartingSoon = isStartingSoon(schedule?.dateStartAt, startingSoonThresholdDays);
        return <li key={classInstance.id} id={getClassInstanceElementId(classInstance.id)} className={styles.item}><AddToCartButton classInstance={classInstance} />{showFewSeatsRemaining && <FewSeatsRemainingBadge label={fewSeatsRemainingBadgeLabel} />}{showStartingSoon && <StartingSoonBadge label={startingSoonBadgeLabel} />}<FieldRow label="Seats Available" value={String(classInstance.seatsAvailable)} />{!classMeta ? <Typography variant="bodySmall">No class metadata</Typography> : <><FieldRow label="Class ID" value={classMeta.classId} /><FieldRow label="Instructor User ID" value={classMeta.instructorUserId} /><FieldRow label="Instructor Name" value={classMeta.instructorName} /><FieldRow label="Registration Deadline" value={classMeta.registrationDeadlineAt} /><FieldRow label="Date Start" value={schedule?.dateStartAt ?? null} /><FieldRow label="Date End" value={schedule?.dateEndAt ?? null} /><FieldRow label="Timezone" value={schedule?.timezone ?? null} /><FieldRow label="Building ID" value={classMeta.buildingId} /><Typography variant="bodySmall">Meeting Times:</Typography>{meetingTimes.length === 0 ? <Typography variant="bodySmall">—</Typography> : <ul className={styles.meetingTimes}>{meetingTimes.map((meeting, index) => <li key={`${classInstance.id}-meeting-${index}`}><Typography variant="bodySmall">{[meeting.day, meeting.timeStartAt && meeting.timeEndAt ? `${meeting.timeStartAt}–${meeting.timeEndAt}` : meeting.timeStartAt ?? meeting.timeEndAt].filter(Boolean).join(" ") || "\u2014"}</Typography></li>)}</ul>}</>}</li>;
      };
      $[6] = fewSeatsRemainingBadgeLabel;
      $[7] = fewSeatsRemainingThreshold;
      $[8] = startingSoonBadgeLabel;
      $[9] = startingSoonThresholdDays;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    t1 = product.classInstances.map(t2);
    $[0] = fewSeatsRemainingBadgeLabel;
    $[1] = fewSeatsRemainingThreshold;
    $[2] = product.classInstances;
    $[3] = startingSoonBadgeLabel;
    $[4] = startingSoonThresholdDays;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[11] !== t1) {
    t2 = <><ul className={styles.list}>{t1}</ul></>;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
}

/**
 * Class schedule for the course page. Prefers RSC-prefetched
 * products; falls back to a server action for missing keys.
 */
export function Schedule(t0) {
  const $ = _c(41);
  const {
    course,
    coursePageSection,
    initialPart: t1,
    courseOfferingProducts
  } = t0;
  const initialPart = t1 === undefined ? null : t1;
  const selectionsByCourse = useAtomValue(courseOfferingSelectionsAtom);
  const selectedModalityId = useAtomValue(learningFormatAtom);
  const selectedCampusId = useAtomValue(selectedCampusAtom);
  const [fetchedByKey, setFetchedByKey] = useState(null);
  const didScrollToClassHashRef = useRef(false);
  let t2;
  if ($[0] !== coursePageSection?.keyedCopy) {
    t2 = coursePageSection?.keyedCopy ?? [];
    $[0] = coursePageSection?.keyedCopy;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const keyedCopy = t2;
  let campusId;
  let courseOfferingId;
  let needsCampus;
  let offering;
  let t3;
  if ($[2] !== course || $[3] !== initialPart || $[4] !== selectedCampusId || $[5] !== selectedModalityId || $[6] !== selectionsByCourse) {
    const selectedOfferingKey = resolveSelectedOfferingKey({
      courseId: course._id,
      courseOfferings: course.courseOfferings,
      selectedModalityId,
      selectionsByCourse,
      initialPart
    });
    offering = getActiveOffering({
      course,
      selectedModalityId,
      selectedOfferingKey
    });
    let t4;
    if ($[12] !== course.modalityOfferings || $[13] !== selectedModalityId) {
      t4 = course.modalityOfferings?.find(t5 => {
        const {
          modality
        } = t5;
        return modality?._id === selectedModalityId;
      });
      $[12] = course.modalityOfferings;
      $[13] = selectedModalityId;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    const selectedModalityOffering = t4;
    needsCampus = selectedModalityOffering?.modality?.price?.displayMode === "campusLocation";
    campusId = needsCampus ? selectedCampusId : null;
    courseOfferingId = offering?.courseOfferingId ?? null;
    t3 = courseOfferingId != null ? courseOfferingProductKey(courseOfferingId, campusId) : null;
    $[2] = course;
    $[3] = initialPart;
    $[4] = selectedCampusId;
    $[5] = selectedModalityId;
    $[6] = selectionsByCourse;
    $[7] = campusId;
    $[8] = courseOfferingId;
    $[9] = needsCampus;
    $[10] = offering;
    $[11] = t3;
  } else {
    campusId = $[7];
    courseOfferingId = $[8];
    needsCampus = $[9];
    offering = $[10];
    t3 = $[11];
  }
  const productKey = t3;
  const prefetchedProduct = productKey != null && productKey in courseOfferingProducts ? courseOfferingProducts[productKey] : undefined;
  const canResolveProduct = Boolean(offering?.showClassSchedule) && Boolean(courseOfferingId) && !(needsCampus && !campusId);
  const needsFetch = canResolveProduct && prefetchedProduct === undefined;
  const fetchedProduct = productKey != null && fetchedByKey?.key === productKey ? fetchedByKey.product : null;
  const isLoading = needsFetch && fetchedByKey?.key !== productKey;
  const product = prefetchedProduct !== undefined ? prefetchedProduct : fetchedProduct;
  let t4;
  if ($[15] !== keyedCopy) {
    t4 = getFewSeatsRemainingThreshold(keyedCopy);
    $[15] = keyedCopy;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const fewSeatsRemainingThreshold = t4;
  let t5;
  if ($[17] !== keyedCopy) {
    t5 = getKeyedCopy(keyedCopy, CLASS_SCHEDULE_COPY.fewSeatsRemaining.badge);
    $[17] = keyedCopy;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const fewSeatsRemainingBadgeLabel = t5;
  let t6;
  if ($[19] !== keyedCopy) {
    t6 = getStartingSoonThresholdDays(keyedCopy);
    $[19] = keyedCopy;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const startingSoonThresholdDays = t6;
  let t7;
  if ($[21] !== keyedCopy) {
    t7 = getKeyedCopy(keyedCopy, CLASS_SCHEDULE_COPY.startingSoon.badge);
    $[21] = keyedCopy;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const startingSoonBadgeLabel = t7;
  let t8;
  let t9;
  if ($[23] !== campusId || $[24] !== courseOfferingId || $[25] !== needsFetch || $[26] !== productKey) {
    t8 = () => {
      if (!needsFetch || !courseOfferingId || productKey == null) {
        return;
      }
      let cancelled = false;
      fetchCourseOfferingProductWithSchedule(courseOfferingId, campusId).then(result => {
        if (!cancelled) {
          setFetchedByKey({
            key: productKey,
            product: result
          });
        }
      });
      return () => {
        cancelled = true;
      };
    };
    t9 = [needsFetch, courseOfferingId, campusId, productKey];
    $[23] = campusId;
    $[24] = courseOfferingId;
    $[25] = needsFetch;
    $[26] = productKey;
    $[27] = t8;
    $[28] = t9;
  } else {
    t8 = $[27];
    t9 = $[28];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[29] !== isLoading || $[30] !== product) {
    t10 = () => {
      if (!product || isLoading || didScrollToClassHashRef.current) {
        return;
      }
      const hash = window.location.hash.replace(/^#/, "");
      if (!isClassInstanceHash(hash)) {
        return;
      }
      const classInstanceId = hash.slice(CLASS_INSTANCE_HASH_PREFIX.length);
      const hasClass = product.classInstances.some(classInstance => classInstance.id === classInstanceId);
      if (!hasClass) {
        return;
      }
      didScrollToClassHashRef.current = true;
      scrollToClassInstance(hash);
    };
    t11 = [product, isLoading];
    $[29] = isLoading;
    $[30] = product;
    $[31] = t10;
    $[32] = t11;
  } else {
    t10 = $[31];
    t11 = $[32];
  }
  useEffect(t10, t11);
  if (!offering?.showClassSchedule) {
    return null;
  }
  let t12;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Typography variant="h5" as="h2">Schedule</Typography>;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== fewSeatsRemainingBadgeLabel || $[35] !== fewSeatsRemainingThreshold || $[36] !== isLoading || $[37] !== product || $[38] !== startingSoonBadgeLabel || $[39] !== startingSoonThresholdDays) {
    t13 = <section className={styles.section} aria-label="Schedule">{t12}{isLoading ? <Typography variant="bodyMd">Loading schedule…</Typography> : !product ? <Typography variant="bodyMd">Schedule not found.</Typography> : <ScheduleTable product={product} fewSeatsRemainingBadgeLabel={fewSeatsRemainingBadgeLabel} fewSeatsRemainingThreshold={fewSeatsRemainingThreshold} startingSoonBadgeLabel={startingSoonBadgeLabel} startingSoonThresholdDays={startingSoonThresholdDays} />}</section>;
    $[34] = fewSeatsRemainingBadgeLabel;
    $[35] = fewSeatsRemainingThreshold;
    $[36] = isLoading;
    $[37] = product;
    $[38] = startingSoonBadgeLabel;
    $[39] = startingSoonThresholdDays;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  return t13;
}